/**
 * Size Selector Web Component
 *
 * Handles EU/UK size unit toggle for product size options.
 * Switches between EU and UK sizes stored in variant metafields.
 */

if (!customElements.get('size-selector')) {
  customElements.define(
    'size-selector',
    class SizeSelector extends HTMLElement {
      abortController = undefined;
      static STORAGE_KEY = 'lugina_size_unit_preference';
      debug = false; // Enable debug logging

      constructor() {
        super();
        this.elements = {};
        this.currentUnit = this.getSavedUnitPreference() || 'eu'; // Load from localStorage or default to EU
      }

      log(...args) {
        if (this.debug) {
          console.log('[SizeSelector]', ...args);
        }
      }

      error(...args) {
        console.error('[SizeSelector]', ...args);
      }

      connectedCallback() {
        this.log('connectedCallback - currentUnit:', this.currentUnit);
        this.abortController = new AbortController();
        this.initElements();
        this.initEventListeners();
        this.restoreUnitPreference();
      }

      /**
       * Get saved unit preference from localStorage
       * @returns {string|null} - 'eu' or 'uk' or null
       */
      getSavedUnitPreference() {
        try {
          return localStorage.getItem(SizeSelector.STORAGE_KEY);
        } catch (error) {
          console.error('Error reading from localStorage:', error);
          return null;
        }
      }

      /**
       * Save unit preference to localStorage
       * @param {string} unit - 'eu' or 'uk'
       */
      saveUnitPreference(unit) {
        try {
          localStorage.setItem(SizeSelector.STORAGE_KEY, unit);
        } catch (error) {
          console.error('Error writing to localStorage:', error);
        }
      }

      /**
       * Restore unit preference from localStorage on page load
       */
      restoreUnitPreference() {
        this.log('🔧 restoreUnitPreference - currentUnit:', this.currentUnit);
        this.log('  Number of unit buttons:', this.elements.unitButtons.length);

        // Update button states based on saved preference
        this.elements.unitButtons.forEach((btn, index) => {
          const isActive = btn.dataset.unit === this.currentUnit;
          btn.classList.toggle('size-selector__unit-button--active', isActive);
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          this.log(`  Button ${index} (${btn.dataset.unit}): ${isActive ? 'ACTIVE' : 'inactive'}`);
        });

        // Update size labels based on saved preference
        this.updateSizeLabels();
      }

      disconnectedCallback() {
        // Cleanup event listeners
        this.abortController.abort();
      }

      /**
       * Refresh the component after DOM changes (e.g., after morphing)
       * This re-initializes element references and event listeners
       */
      refresh() {
        this.log('🔄 refresh() called');
        this.log('  currentUnit BEFORE refresh:', this.currentUnit);

        // Abort old event listeners
        this.abortController.abort();

        // Create new abort controller
        this.abortController = new AbortController();

        // IMPORTANT: Re-load current unit from localStorage
        // After morphing, the new component might have default 'eu' but we want to keep user's choice
        const savedUnit = this.getSavedUnitPreference();
        this.log('  Saved unit from localStorage:', savedUnit);
        this.currentUnit = savedUnit || 'eu';
        this.log('  currentUnit AFTER reload:', this.currentUnit);

        // Re-initialize element references
        this.initElements();

        // CRITICAL: Re-bind event listeners AND restore UI state AFTER the DOM morphing is complete
        // We need to wait for both the morph AND browser reflow to complete
        // Double requestAnimationFrame ensures we run after all DOM changes
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.log('  ⏰ Applying changes after morph (double RAF)...');

            // Re-initialize element references again (DOM might have changed)
            this.initElements();

            // Re-initialize event listeners (bind to the new DOM elements)
            this.initEventListeners();

            // Restore unit preference (set button states)
            this.restoreUnitPreference();

            this.log('  refresh() complete');
          });
        });
      }

      /**
       * Get selected option values (for pub/sub compatibility)
       */
      get selectedOptionValues() {
        const checkedInput = this.querySelector('input[type="radio"]:checked');
        return checkedInput ? [checkedInput.dataset.optionValueId] : [];
      }

      /**
       * Initialize DOM element references
       */
      initElements() {
        this.elements = {
          unitButtons: this.querySelectorAll('[data-unit]'),
          sizeInputs: this.querySelectorAll('input[type="radio"][data-eu-size][data-uk-size]'),
          sizeLabels: this.querySelectorAll('[data-size-label]'),
          currentSizeDisplay: this.querySelector('[data-current-size]'),
          ukSizeInput: this.querySelector('[data-uk-size-input]')
        };
      }

      /**
       * Initialize event listeners
       */
      initEventListeners() {
        // Unit toggle buttons
        this.elements.unitButtons.forEach(button => {
          button.addEventListener(
            'click',
            this.handleUnitToggle.bind(this),
            { signal: this.abortController.signal }
          );
        });

        // Size selection
        this.elements.sizeInputs.forEach(input => {
          input.addEventListener(
            'change',
            this.handleSizeChange.bind(this),
            { signal: this.abortController.signal }
          );
        });
      }

      /**
       * Handle unit toggle (EU/UK)
       * @param {Event} event - Click event
       */
      handleUnitToggle(event) {
        const button = event.currentTarget;
        const unit = button.dataset.unit;

        this.log('🔘 Click on unit toggle:', unit);
        this.log('  currentUnit:', this.currentUnit);
        this.log('  Button element:', button.textContent.trim());

        if (unit === this.currentUnit) {
          this.log('  ⚠️ Already on this unit, no change needed');
          return; // Already on this unit
        }

        this.log('  ✅ Switching from', this.currentUnit, 'to', unit);

        // Update current unit
        this.currentUnit = unit;

        // Save preference to localStorage
        this.saveUnitPreference(unit);
        this.log('  Saved to localStorage:', unit);

        // Update button states
        this.elements.unitButtons.forEach(btn => {
          const isActive = btn.dataset.unit === unit;
          btn.classList.toggle('size-selector__unit-button--active', isActive);
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.log('  Updated button states');

        // Update size labels
        this.updateSizeLabels();
        this.log('  Updated size labels to', unit, 'format');
      }

      /**
       * Update size labels based on current unit
       */
      updateSizeLabels() {
        this.log('  📝 updateSizeLabels - currentUnit:', this.currentUnit);
        this.log('    Number of size inputs:', this.elements.sizeInputs.length);

        this.elements.sizeInputs.forEach((input, index) => {
          const label = this.elements.sizeLabels[index];
          if (!label) return;

          const euSize = input.dataset.euSize;
          const ukSize = input.dataset.ukSize;

          // Display EU or UK size
          const displaySize = this.currentUnit === 'uk' && ukSize ? ukSize : euSize;
          label.textContent = displaySize;

          if (index === 0) {
            this.log(`    Example: EU=${euSize}, UK=${ukSize}, display=${displaySize}`);
          }
        });

        // Update current size display
        this.updateCurrentSizeDisplay();
        this.log('  📝 updateSizeLabels complete');
      }

      /**
       * Update the current size display in the header
       */
      updateCurrentSizeDisplay() {
        this.log('    📊 updateCurrentSizeDisplay');

        if (!this.elements.currentSizeDisplay) {
          this.log('      ⚠️ No currentSizeDisplay element');
          return;
        }

        const checkedInput = this.querySelector('input[type="radio"]:checked');
        if (!checkedInput) {
          this.log('      ⚠️ No checked input');
          return;
        }

        const euSize = checkedInput.dataset.euSize;
        const ukSize = checkedInput.dataset.ukSize;

        // Display EU or UK size based on current unit
        const displaySize = this.currentUnit === 'uk' && ukSize ? ukSize : euSize;
        this.elements.currentSizeDisplay.textContent = displaySize;
        this.log(`      Display: ${displaySize} (EU=${euSize}, UK=${ukSize}, unit=${this.currentUnit})`);
      }

      /**
       * Handle size selection change
       * @param {Event} event - Change event
       */
      handleSizeChange(event) {
        const input = event.currentTarget;
        const euSize = input.dataset.euSize;
        const ukSize = input.dataset.ukSize;

        this.log('📏 Size selection changed');
        this.log('  EU Size:', euSize);
        this.log('  UK Size:', ukSize);
        this.log('  Current unit:', this.currentUnit);

        // Update current size display
        this.updateCurrentSizeDisplay();

        // Update hidden UK size input field
        if (this.elements.ukSizeInput) {
          this.elements.ukSizeInput.value = ukSize || '';
          this.log('  Updated hidden UK size input:', ukSize);
        }

        // Publish variant change event via FoxTheme pubsub
        // This triggers both:
        // 1. product-info.js - AJAX update of price, inventory, etc.
        // 2. product-variant-selector.js - Update color links to match new size
        if (window.FoxTheme && window.FoxTheme.pubsub) {
          this.log('  Publishing optionValueSelectionChange event with size:', euSize);
          window.FoxTheme.pubsub.publish(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            {
              data: {
                event,
                target: input,
                selectedOptionValues: this.selectedOptionValues,
                selectedSize: euSize, // Pass EU size for color variant matching
                selectedSizeUk: ukSize // Optional: UK size
              }
            }
          );
        } else {
          this.error('  ❌ FoxTheme.pubsub not available!');
        }
      }
    }
  );
}
